'use client';

import React from 'react';
import Image from 'next/image';
import { useLocale } from '@/contexts/LocaleContext';
import { useTranslations } from '@/hooks/useTranslations';
import { LocalizedLink } from './LocalizedLink';
import type { SuggestionItem } from '@/app/api/casino-suggest/route';
import { SafeGameImage } from '@/components/SafeGameImage';
import { getGameImageCandidates } from '@/lib/game-image';

type SearchAutocompleteProps = {
  className?: string;
  inputClassName?: string;
  onSubmit?: () => void;
};

const MIN_CHARS = 3;

export function SearchAutocomplete({
  className,
  inputClassName,
  onSubmit,
}: SearchAutocompleteProps) {
  const locale = useLocale();
  const { t } = useTranslations();
  const [query, setQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [data, setData] = React.useState<{
    casinos: SuggestionItem[];
    providers: SuggestionItem[];
    games: SuggestionItem[];
  }>({ casinos: [], providers: [], games: [] });
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const trimmedQuery = query.trim();
  const canSuggest = trimmedQuery.length >= MIN_CHARS;
  const hasResults =
    data.casinos.length > 0 || data.providers.length > 0 || data.games.length > 0;

  React.useEffect(() => {
    if (!canSuggest) {
      setData({ casinos: [], providers: [], games: [] });
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/casino-suggest?search=${encodeURIComponent(trimmedQuery)}&lang=${encodeURIComponent(locale)}`,
          { signal: controller.signal },
        );
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const json = (await res.json()) as {
          casinos?: SuggestionItem[];
          providers?: SuggestionItem[];
          games?: SuggestionItem[];
        };
        setData({
          casinos: Array.isArray(json.casinos) ? json.casinos : [],
          providers: Array.isArray(json.providers) ? json.providers : [],
          games: Array.isArray(json.games) ? json.games : [],
        });
      } catch {
        setData({ casinos: [], providers: [], games: [] });
      } finally {
        setIsLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [canSuggest, trimmedQuery, locale]);

  const handleItemClick = () => {
    setIsOpen(false);
    onSubmit?.();
  };

  return (
    <form
      action={locale === 'en' ? '/' : `/${locale}`}
      method="get"
      className={className}
      onSubmit={() => handleItemClick()}
    >
      <span className="absolute inset-y-0 left-3 flex items-center text-slate-400" aria-hidden>
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path strokeLinecap="round" d="M20 20l-3.5-3.5" />
        </svg>
      </span>
      <input
        name="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setIsOpen(true)}
        onBlur={(e) => {
          const next = e.relatedTarget;
          if (dropdownRef.current && next && dropdownRef.current.contains(next as Node)) {
            return;
          }
          setTimeout(() => setIsOpen(false), 120);
        }}
        className={inputClassName}
        placeholder={t('search.placeholder')}
        aria-label={t('search.placeholderShort')}
      />
      {isOpen && canSuggest ? (
        <div
          ref={dropdownRef}
          onMouseDown={(e) => e.preventDefault()}
          className="scrollbar-styled absolute left-0 right-0 top-[calc(100%+8px)] z-[60] max-h-96 overflow-auto rounded-2xl border border-white/10 bg-[#15192b] p-2 shadow-xl"
        >
          {isLoading ? (
            <p className="px-3 py-2 text-xs text-slate-400">{t('search.loadingSuggestions')}</p>
          ) : !hasResults ? (
            <p className="px-3 py-2 text-xs text-slate-400">{t('filters.nothingFoundTitle')}</p>
          ) : (
            <div className="space-y-4">
              {data.casinos.length > 0 && (
                <div>
                  <p className="mb-1 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {t('nav.casinos')}
                  </p>
                  <ul className="space-y-1">
                    {data.casinos.map((item) => (
                      <li key={`casino-${item.slug}`}>
                        <LocalizedLink
                          href={item.href}
                          className="flex items-center gap-3 rounded-xl px-3 py-2 transition hover:bg-white/10"
                          onClick={handleItemClick}
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-[#0b0d19]">
                            {'logoUrl' in item && item.logoUrl ? (
                              <Image
                                src={item.logoUrl}
                                alt={`${item.name} logo`}
                                width={32}
                                height={32}
                                sizes="32px"
                                className="h-full w-full object-contain p-1"
                              />
                            ) : (
                              <span className="text-[10px] font-bold text-[#ffb74d]">
                                {item.name
                                  .split(/\s+/)
                                  .filter(Boolean)
                                  .slice(0, 2)
                                  .map((part) => part.charAt(0).toUpperCase())
                                  .join('') || 'C'}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-white">{item.name}</p>
                          </div>
                          {'rating' in item && (
                            <div className="shrink-0 text-xs font-semibold text-[#ffb74d]">
                              {Number(item.rating).toFixed(1)}
                            </div>
                          )}
                        </LocalizedLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.games.length > 0 && (
                <div>
                  <p className="mb-1 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {t('nav.games')}
                  </p>
                  <ul className="space-y-1">
                    {data.games.map((item) => (
                      <li key={`game-${item.slug}`}>
                        <LocalizedLink
                          href={item.href}
                          className="flex items-center gap-3 rounded-xl px-3 py-2 transition hover:bg-white/10"
                          onClick={handleItemClick}
                        >
                          <div className="flex h-8 w-8 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-[#0b0d19]">
                            {'imageUrl' in item ? (
                              <SafeGameImage
                                src={item.imageUrl}
                                srcCandidates={getGameImageCandidates(item.providerSlug, item.slug, 'thumb')}
                                alt={item.name}
                                imageClassName="h-full w-full object-cover"
                                placeholderTitle={item.name}
                                placeholderContainerClassName="h-full w-full"
                              />
                            ) : null}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-white">{item.name}</p>
                            {'provider' in item && item.provider && (
                              <p className="truncate text-[10px] text-slate-400">{item.provider}</p>
                            )}
                          </div>
                        </LocalizedLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.providers.length > 0 && (
                <div>
                  <p className="mb-1 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {t('nav.gameProviders')}
                  </p>
                  <ul className="space-y-1">
                    {data.providers.map((item) => (
                      <li key={`provider-${item.slug}`}>
                        <LocalizedLink
                          href={item.href}
                          className="flex items-center gap-3 rounded-xl px-3 py-2 transition hover:bg-white/10"
                          onClick={handleItemClick}
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-[#0b0d19]">
                            {'logoUrl' in item && item.logoUrl ? (
                              <Image
                                src={item.logoUrl}
                                alt={`${item.name} logo`}
                                width={32}
                                height={32}
                                sizes="32px"
                                className="h-full w-full object-contain p-1"
                              />
                            ) : (
                              <span className="text-[10px] font-bold text-[#ffb74d]">
                                {item.name
                                  .split(/\s+/)
                                  .filter(Boolean)
                                  .slice(0, 2)
                                  .map((part) => part.charAt(0).toUpperCase())
                                  .join('') || 'P'}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-white">{item.name}</p>
                          </div>
                        </LocalizedLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </form>
  );
}
